import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { CheckItem } from './CheckItem';
import type { ComplianceIssue } from '../../types/scanner';

interface CheckListProps {
  issues: ComplianceIssue[];
}

type FilterType = 'all' | 'critical' | 'warning';

export const CheckList: React.FC<CheckListProps> = ({ issues }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');

  const filteredIssues = useMemo(() => {
    let filtered = issues;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (issue) =>
          issue.code.toLowerCase().includes(searchLower) ||
          issue.message.toLowerCase().includes(searchLower)
      );
    }

    if (filter !== 'all') {
      filtered = filtered.filter((issue) => issue.severity === filter);
    }

    return filtered;
  }, [issues, search, filter]);

  const groupedIssues = useMemo(() => {
    const groups = new Map<string, ComplianceIssue[]>();

    for (const issue of filteredIssues) {
      const key = issue.severity;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(issue);
    }

    return groups;
  }, [filteredIssues]);

  const counts = {
    all: issues.length,
    critical: issues.filter((i) => i.severity === 'critical').length,
    warning: issues.filter((i) => i.severity === 'warning').length,
  };

  if (issues.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-muted)' }}>
        <p style={{ fontWeight: 600 }}>No compliance issues found!</p>
        <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>Your website passed all checks.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* Search and Filter */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{
            position: 'absolute',
            left: 'var(--space-3)',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-muted)',
          }} />
          <input
            type="text"
            placeholder="Search issues..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            style={{ paddingLeft: 'calc(var(--space-3) + 24px)' }}
          />
        </div>

        <div style={{
          display: 'flex',
          border: 'var(--border-thick)',
        }}>
          {(['all', 'critical', 'warning'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-sm)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: filter === type ? 'var(--color-fg)' : 'var(--color-bg)',
                color: filter === type ? 'var(--color-bg)' : 'var(--color-fg)',
                transition: 'all var(--transition-fast)',
              }}
            >
              {type === 'all' ? 'All' : type === 'critical' ? 'Critical' : 'Warnings'}
              <span style={{ marginLeft: 'var(--space-1)', opacity: 0.7 }}>({counts[type]})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grouped Issues */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {Array.from(groupedIssues.entries())
          .sort((a, b) => {
            if (a[0] === 'critical' && b[0] !== 'critical') return -1;
            if (a[0] !== 'critical' && b[0] === 'critical') return 1;
            return 0;
          })
          .map(([group, groupIssues]) => (
            <div key={group}>
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-sm)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-muted)',
                marginBottom: 'var(--space-2)',
              }}>
                {group === 'critical' ? 'Critical Issues' : 'Warnings'} ({groupIssues.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {groupIssues.map((issue, index) => (
                  <CheckItem key={`${issue.code}-${index}`} issue={issue} />
                ))}
              </div>
            </div>
          ))}
      </div>

      {filteredIssues.length === 0 && (
        <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-muted)' }}>
          No issues match your search.
        </div>
      )}
    </div>
  );
};

export default CheckList;
